import { useEffect } from "react";
import { Container, Card } from "react-bootstrap";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../css/Legal.css";

const TermsOfUse = () => {
  useEffect(() => {
    document.title = "JACAi – AI Chatbot";
  }, []);
    
  return (
    <>
      <Header />

      <div className="legal-page">
        <Container className="legal-container py-5">
          <Card className="legal-card">
            <div className="legal-card-inner">

              <h1 className="mb-2">Terms of Use</h1>
              <p className="text-muted mb-4">
                <strong>Last Updated:</strong> November 14, 2025
              </p>

              <h4 className="mt-4">1. Acceptance</h4>
              <p>
                By using this chatbot (“Service”), you agree to these Terms. If you
                do not agree, please stop using the Service.
              </p>

              <h4 className="mt-4">2. Description of the Service</h4>
              <p>
                This Service is an experimental large language model (LLM) chatbot
                developed by <strong>Marius Cirjan</strong>. Messages you send are
                stored in Google Firebase.
              </p>

              <h4 className="mt-4">3. User Responsibilities</h4>
              <ul>
                <li>Do not send illegal, harmful, or abusive content</li>
                <li>Do not submit personal data of others without permission</li>
                <li>Do not attempt to hack, disrupt, or exploit the Service</li>
                <li>Do not use the Service for unlawful activities</li>
              </ul>

              <h4 className="mt-4">4. Data Storage</h4>
              <p>
                By using the Service, you agree that your messages will be stored in
                Firebase and may be used to operate and improve the chatbot.
              </p>

              <h4 className="mt-4">5. No Guarantees</h4>
              <p>
                The chatbot may generate incorrect, incomplete, or misleading
                information. The Service is provided <strong>“as is”</strong> with
                no warranties.
              </p>

              <h4 className="mt-4">6. Limitation of Liability</h4>
              <p>
                To the fullest extent allowed by law, I am not responsible for:
              </p>
              <ul>
                <li>Errors in chatbot responses</li>
                <li>Loss of data</li>
                <li>Decisions made based on chatbot output</li>
              </ul>

              <h4 className="mt-4">7. Termination</h4>
              <p>
                I may suspend or terminate access to the Service at any time,
                especially if you violate these Terms.
              </p>

              <h4 className="mt-4">8. Governing Law</h4>
              <p>
                These Terms are governed by Romanian law and applicable EU
                regulations. Any disputes will be resolved in Romania.
              </p>

              <h4 className="mt-4">9. Changes</h4>
              <p>
                I may update these Terms. Changes will appear on this page.
              </p>

              <h4 className="mt-4">10. AI Disclaimer</h4>
              <p>
                The Service uses an artificial intelligence model to generate
                responses. These responses may be inaccurate, incomplete, or
                misleading. You should not rely on any output as factual or correct
                without independent verification.
              </p>

              <h4 className="mt-4">11. No Professional Advice</h4>
              <p>
                The chatbot may generate content related to medical, legal,
                financial, psychological, or other professional fields. This content
                is <strong>not professional advice</strong> and must not be used as
                a substitute for consultation with a qualified expert. Always seek
                proper professional advice before making decisions.
              </p>

              <h4 className="mt-4">12. Model Transparency</h4>
              <p>
                When you use the Service, your messages may be stored and analyzed
                to improve system quality, accuracy, and safety. Data handling
                follows all applicable GDPR requirements as described in the Privacy
                Policy.
              </p>

              <h4 className="mt-4">13. Contact</h4>
              <p>
                <strong>Marius Cirjan</strong> <br />
                <a href="mailto:cirjan.marius7@gmail.com">
                  cirjan.marius7@gmail.com
                </a>
              </p>

              <h4 className="mt-4">14. Use of Wikipedia Content</h4>
              <p>
                This Service uses processed and reformatted text extracted from{" "}
                <strong>Wikipedia</strong> for educational and informational purposes.
                Wikipedia content is licensed under the{" "}
                <strong>Creative Commons Attribution-ShareAlike License (CC BY-SA 4.0)</strong>.
              </p>
              <p>
                The chatbot may summarize, transform, or restructure Wikipedia-derived
                text using AI models. Such processed content does not represent the
                original articles verbatim. You can access the original Wikipedia articles at:{" "}
                <a
                  href="https://www.wikipedia.org"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.wikipedia.org
                </a>.
              </p>
              <p>
                Attribution to Wikipedia is provided in compliance with the CC BY-SA 4.0
                license. If you reuse or distribute content derived from this Service,
                you may be required to comply with the same license terms.
              </p>
              <h4 className="mt-4">15. Use of ESCO Classification Data</h4>
              <p>
                This Service uses processed and reformatted data extracted from the 
                <strong>ESCO Classification</strong> (European Skills, Competences, Qualifications and Occupations),
                provided by the European Commission.
              </p>
              <p>
                In accordance with ESCO usage conditions, we acknowledge the source by stating:
                <em>"This service uses the ESCO classification of the European Commission."</em>
              </p>
              <p>
                ESCO data may be adapted, transformed, summarized, or integrated into AI-generated
                outputs for the purpose of improving educational, informational, and career-related
                assistance. Any such processed content does not represent the original ESCO data
                verbatim.
              </p>
              <p>
                Modified or adapted versions of ESCO data are clearly indicated as such within this
                Service, as required by the European Commission’s ESCO usage guidelines.
              </p>
              <p>
                The European Commission does not guarantee that the ESCO data, whether accessed through
                the ESCO website or downloaded files, is fully accurate, up-to-date, or complete.
                The Commission shall not be liable for any consequences resulting from the use,
                reuse, or deployment of ESCO data within this Service.
              </p>
              <p>
                For more information about the official ESCO classification, please visit:
                <a
                  href="https://esco.ec.europa.eu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://esco.ec.europa.eu
                </a>.
              </p>
              <h4 className="mt-4">16. Use of O*NET® Database Content</h4>
              <p>
                This Service incorporates processed and reformatted data from the 
                <strong>O*NET® Database</strong>, provided by the U.S. Department of Labor, 
                Employment and Training Administration (USDOL/ETA).
              </p>

              <p>
                In accordance with the O*NET Data Usage requirements and the 
                <strong>Creative Commons Attribution 4.0 International (CC BY 4.0)</strong> license,
                we acknowledge the source by stating:
                <em>
                  "O*NET® data is provided by the U.S. Department of Labor, Employment and Training 
                  Administration (USDOL/ETA). Used under the CC BY 4.0 license."
                </em>
              </p>

              <p>
                The O*NET name and trademark are owned by USDOL/ETA and are used here solely to 
                provide attribution. This Service is <strong>not endorsed</strong> by the U.S. Department of Labor, 
                nor does it claim any affiliation or approval.
              </p>

              <p>
                O*NET data used within this Service may be adapted, transformed, summarized, or 
                integrated into AI-generated responses for the purpose of improving career guidance,
                educational assistance, and informational features. Any generated or reformatted content 
                does <strong>not</strong> represent the original O*NET database verbatim.
              </p>

              <p>
                The U.S. Department of Labor does not guarantee that the O*NET data is complete, 
                accurate, or up-to-date. The Department of Labor shall not be held liable for any consequences 
                arising from the use, reuse, or interpretation of O*NET-based information provided through 
                this Service.
              </p>

              <p>
                For more information about the official O*NET database, please visit:
                <a
                  href="https://www.onetonline.org"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.onetonline.org
                </a>.
              </p>

              <h4 className="mt-4">16. Use of OpenStax Educational Content</h4>
              <p>
                This Service uses processed, adapted, or reformatted educational material derived from
                <strong>OpenStax</strong> textbooks published by Rice University.
                OpenStax content is made available under the
                <strong>Creative Commons Attribution 4.0 International (CC BY 4.0)</strong> license.
              </p>

              <p>
                In accordance with the CC BY 4.0 license requirements, we acknowledge the source by stating:
                <em>"This service uses content adapted from OpenStax, Rice University, licensed under CC BY 4.0."</em>
              </p>

              <p>
                Any OpenStax-derived material used in this Service may have been modified, reformatted,
                summarized, translated, or integrated into AI-generated outputs. Such processed content does
                <strong>not</strong> represent the original OpenStax text verbatim.
              </p>

              <p>
                OpenStax and Rice University are not responsible for any changes made to the original material,
                nor do they endorse this Service or its interpretations of the content.
              </p>

              <p>
                The official OpenStax textbooks and materials can be accessed freely at:
                <a href="https://openstax.org" target="_blank" rel="noopener noreferrer">
                  https://openstax.org
                </a>.
              </p>
            </div>
          </Card>
        </Container>
      </div>

      <Footer />
    </>
  );
};

export default TermsOfUse;